import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, type Editor, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { type ReactNode, useEffect, useState } from 'react';

import { Button, inputCls } from '../ui';
import { PolicyLink } from './policy-link';
import './policy-editor.css';

export interface PolicyLinkTarget {
  slug: string;
  title: string;
}

/**
 * 약관 본문 리치 에디터 (TipTap) — 제목 1~3·굵게·기울임·밑줄·목록·다른 약관 링크·바깥 링크·표.
 * HTML 을 내고, 서버가 허용 태그만 남겨 저장한다. 이미지는 넣지 않는다(약관에 필요 없고 앱이 그리지 않는다)
 */
export function PolicyEditor({
  value,
  onChange,
  editable,
  policies,
}: {
  value: string;
  onChange(html: string): void;
  editable: boolean;
  /** 다른 약관 링크로 고를 약관 */
  policies: PolicyLinkTarget[];
}) {
  const editor = useEditor({
    extensions: [
      // StarterKit 의 기본 Link 는 끈다 — 다른 약관·바깥 링크를 하나로 다루는 PolicyLink 와 겹친다
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Placeholder.configure({ placeholder: '제1조 (목적) …' }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      PolicyLink,
    ],
    content: value,
    editable,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  // 버전·언어를 바꾸면 내용을 갈아 끼운다 (입력 중인 값과 같으면 그대로 — 커서가 튀지 않게)
  useEffect(() => {
    if (editor && value !== editor.getHTML())
      editor.commands.setContent(value, { emitUpdate: false });
  }, [value, editor]);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editable, editor]);

  return (
    <div className="flex flex-col rounded-md border border-border">
      {editable && editor && <Toolbar editor={editor} policies={policies} />}
      <div className="policy-editor max-h-[calc(100vh-320px)] min-h-[420px] overflow-y-auto p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor, policies }: { editor: Editor; policies: PolicyLinkTarget[] }) {
  const [popover, setPopover] = useState<null | 'policy' | 'external'>(null);
  const [selection, setSelection] = useState<{ from: number; to: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // TipTap v3 는 거래마다 다시 그리지 않는다 — 활성 표시는 useEditorState 로 구독
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      link: e.isActive('policyLink'),
      table: e.isActive('table'),
    }),
  });

  /** 링크는 고른 글자에 단다 — 고른 범위를 기억해 두고 입력 칸을 연다 */
  const openLink = (kind: 'policy' | 'external') => {
    if (popover === kind) {
      setPopover(null);
      return;
    }
    if (editor.state.selection.empty) {
      setNotice('링크를 걸 글자를 먼저 골라 주세요.');
      return;
    }
    setNotice(null);
    const { from, to } = editor.state.selection;
    setSelection({ from, to });
    setPopover(kind);
  };

  const applyPolicyLink = (slug: string) => {
    if (selection) {
      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .setMark('policyLink', { href: `#policy/${slug}`, slug })
        .run();
    }
    setPopover(null);
  };

  const applyExternalLink = (url: string) => {
    if (selection) {
      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .setMark('policyLink', { href: url, slug: null })
        .run();
    }
    setPopover(null);
  };

  const run = (fn: (c: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) =>
    fn(editor.chain().focus()).run();

  return (
    <div className="relative border-b border-border bg-background px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-0.5">
        <Tool
          label="제목 1"
          active={active.h1}
          onClick={() => run((c) => c.toggleHeading({ level: 1 }))}
        >
          H1
        </Tool>
        <Tool
          label="제목 2"
          active={active.h2}
          onClick={() => run((c) => c.toggleHeading({ level: 2 }))}
        >
          H2
        </Tool>
        <Tool
          label="제목 3"
          active={active.h3}
          onClick={() => run((c) => c.toggleHeading({ level: 3 }))}
        >
          H3
        </Tool>
        <Divider />
        <Tool label="굵게" active={active.bold} onClick={() => run((c) => c.toggleBold())}>
          <b>B</b>
        </Tool>
        <Tool label="기울임" active={active.italic} onClick={() => run((c) => c.toggleItalic())}>
          <i className="font-serif">I</i>
        </Tool>
        <Tool
          label="밑줄"
          active={active.underline}
          onClick={() => run((c) => c.toggleUnderline())}
        >
          <u>U</u>
        </Tool>
        <Divider />
        <Tool
          label="글머리 목록"
          active={active.bullet}
          onClick={() => run((c) => c.toggleBulletList())}
        >
          • 목록
        </Tool>
        <Tool
          label="번호 목록"
          active={active.ordered}
          onClick={() => run((c) => c.toggleOrderedList())}
        >
          1. 목록
        </Tool>
        <Divider />
        <Tool
          label="다른 약관 링크"
          active={popover === 'policy'}
          onClick={() => openLink('policy')}
        >
          약관 링크
        </Tool>
        <Tool
          label="바깥 링크"
          active={popover === 'external'}
          onClick={() => openLink('external')}
        >
          바깥 링크
        </Tool>
        {active.link && (
          <Tool
            label="링크 풀기"
            onClick={() => run((c) => c.extendMarkRange('policyLink').unsetMark('policyLink'))}
          >
            링크 풀기
          </Tool>
        )}
        <Divider />
        <Tool
          label="표 넣기"
          onClick={() => run((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))}
        >
          표 넣기
        </Tool>
        {active.table && (
          <>
            <Tool label="줄 추가" onClick={() => run((c) => c.addRowAfter())}>
              줄+
            </Tool>
            <Tool label="칸 추가" onClick={() => run((c) => c.addColumnAfter())}>
              칸+
            </Tool>
            <Tool label="줄 삭제" onClick={() => run((c) => c.deleteRow())}>
              줄−
            </Tool>
            <Tool label="칸 삭제" onClick={() => run((c) => c.deleteColumn())}>
              칸−
            </Tool>
            <Tool label="표 삭제" onClick={() => run((c) => c.deleteTable())}>
              표 삭제
            </Tool>
          </>
        )}
      </div>
      {notice && <p className="mt-1 px-1 text-xs text-amber-700">{notice}</p>}
      {popover === 'policy' && (
        <div className="absolute left-2 top-full z-20 mt-1 max-h-72 w-72 overflow-y-auto rounded-md border border-border bg-background p-1 shadow-lg">
          {policies.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">링크할 다른 약관이 없어요.</p>
          )}
          {policies.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => applyPolicyLink(p.slug)}
              className="flex w-full flex-col rounded px-2 py-1.5 text-left hover:bg-muted"
            >
              <span className="text-sm font-semibold">{p.title}</span>
              <span className="font-mono text-[11px] text-muted-foreground">/{p.slug}</span>
            </button>
          ))}
        </div>
      )}
      {popover === 'external' && (
        <ExternalLinkForm onSubmit={applyExternalLink} onCancel={() => setPopover(null)} />
      )}
    </div>
  );
}

/** 바깥 주소 입력 — http(s)·mailto 만 (서버도 같은 주소만 남긴다) */
function ExternalLinkForm({
  onSubmit,
  onCancel,
}: {
  onSubmit(url: string): void;
  onCancel(): void;
}) {
  const [url, setUrl] = useState('https://');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="absolute left-2 top-full z-20 mt-1 flex w-96 flex-col gap-2 rounded-md border border-border bg-background p-3 shadow-lg"
      onSubmit={(e) => {
        e.preventDefault();
        const value = url.trim();
        if (!/^(https?:\/\/\S+|mailto:\S+)$/i.test(value)) {
          setError('https:// 또는 mailto: 로 시작하는 주소를 적어 주세요.');
          return;
        }
        onSubmit(value);
      }}
    >
      <input
        autoFocus
        className={inputCls}
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          setError(null);
        }}
        placeholder="https://"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel}>
          취소
        </Button>
        <Button size="sm" type="submit">
          링크 걸기
        </Button>
      </div>
    </form>
  );
}

function Tool({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick(): void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      // 누를 때 에디터 선택이 풀리지 않게
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`h-8 min-w-8 rounded px-2 text-xs font-semibold transition ${
        active ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-5 w-px bg-border" />;
}
